'use client';

import { useState, useEffect } from 'react';
import { api, AllStatsResponse, ScoreStatistics } from '@/lib/api';

/**
 * Hook to fetch and manage score statistics for benchmark comparisons.
 * Fetches all statistics once and provides helper functions to get stats by key.
 * 
 * @param weightProfileId - Optional filter to only include certifications with this typology
 */
export function useScoreStatistics(weightProfileId?: string | null) {
    const [stats, setStats] = useState<AllStatsResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchStats = async () => {
            try {
                setLoading(true);
                const response = await api.getAllScoreStatistics(weightProfileId ?? undefined);
                setStats(response);
                setError(null);
            } catch (err) {
                console.error('Failed to fetch score statistics:', err);
                setError(err instanceof Error ? err.message : 'Failed to fetch statistics');
            } finally {
                setLoading(false);
            }
        };

        fetchStats();
    }, [weightProfileId]);

    /**
     * Get statistics for a specific subcategory.
     * @param cluster - The cluster name (e.g., "ENVIRONMENT")
     * @param categoryName - The category name
     * @param subcategoryName - The subcategory name
     */
    const getSubcategoryStats = (
        cluster: string,
        categoryName: string,
        subcategoryName: string
    ): ScoreStatistics | null => {
        if (!stats) return null;
        const key = `${cluster}::${categoryName}::${subcategoryName}`;
        return stats.subcategories[key] || null;
    };

    /**
     * Get statistics for a specific category.
     * @param cluster - The cluster name (e.g., "ENVIRONMENT")
     * @param categoryName - The category name
     */
    const getCategoryStats = (
        cluster: string,
        categoryName: string
    ): ScoreStatistics | null => {
        if (!stats) return null;
        const key = `${cluster}::${categoryName}`;
        return stats.categories[key] || null;
    };

    /**
     * Get statistics for a specific cluster.
     * @param cluster - The cluster name (e.g., "ENVIRONMENT")
     */
    const getClusterStats = (cluster: string): ScoreStatistics | null => {
        if (!stats) return null;
        return stats.clusters[cluster] || null;
    };

    /**
     * Get overall statistics.
     */
    const getOverallStats = (): ScoreStatistics | null => {
        if (!stats) return null;
        return stats.overall;
    };

    return {
        stats,
        loading,
        error,
        getSubcategoryStats,
        getCategoryStats,
        getClusterStats,
        getOverallStats,
    };
}
